import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAuth } from "./AuthContext";
import {
  getSocialSavedPostsStatus,
  saveSocialPost,
  unsaveSocialPost,
} from "@/lib/socialSavedApi";

const SocialSavedPostsContext = createContext(null);

export function SocialSavedPostsProvider({ children }) {
  const { accessToken } = useAuth();
  const [savedMap, setSavedMap] = useState(() => new Map());
  const knownKeysRef = useRef(new Set());
  const pendingRef = useRef(new Set());
  const flushTimerRef = useRef(null);
  const prevTokenRef = useRef(accessToken);

  useEffect(() => {
    if (prevTokenRef.current === accessToken) return;
    prevTokenRef.current = accessToken;
    setSavedMap(new Map());
    knownKeysRef.current = new Set();
    pendingRef.current = new Set();
    if (flushTimerRef.current) {
      clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
    }
  }, [accessToken]);

  useEffect(() => {
    return () => {
      if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
    };
  }, []);

  const flush = useCallback(async () => {
    if (!accessToken || !pendingRef.current.size) return;
    const ids = [...pendingRef.current];
    pendingRef.current.clear();
    try {
      const { saved } = await getSocialSavedPostsStatus(accessToken, ids);
      setSavedMap((prev) => {
        const next = new Map(prev);
        for (const id of ids) {
          knownKeysRef.current.add(id);
          next.set(id, Boolean(saved?.[String(id)]));
        }
        return next;
      });
    } catch (err) {
      console.error("Failed to load social saved post status:", err);
      for (const id of ids) knownKeysRef.current.delete(id);
    }
  }, [accessToken]);

  const ensureStatus = useCallback(
    (postId) => {
      if (!accessToken || postId == null) return;
      const key = String(postId);
      if (knownKeysRef.current.has(key)) return;
      knownKeysRef.current.add(key);
      pendingRef.current.add(key);
      if (flushTimerRef.current) return;
      flushTimerRef.current = setTimeout(() => {
        flushTimerRef.current = null;
        flush();
      }, 40);
    },
    [accessToken, flush]
  );

  const isSaved = useCallback(
    (postId) => {
      if (postId == null) return false;
      return Boolean(savedMap.get(String(postId)));
    },
    [savedMap]
  );

  const setSavedLocal = useCallback((postId, value) => {
    const key = String(postId);
    knownKeysRef.current.add(key);
    setSavedMap((prev) => {
      const next = new Map(prev);
      next.set(key, Boolean(value));
      return next;
    });
  }, []);

  const toggleSaved = useCallback(
    async (postId) => {
      if (!accessToken || postId == null) {
        throw new Error("Missing auth or post context");
      }
      const key = String(postId);
      const currentlySaved = isSaved(key);
      setSavedLocal(key, !currentlySaved);
      try {
        if (currentlySaved) {
          await unsaveSocialPost(accessToken, key);
          setSavedLocal(key, false);
          return false;
        }
        await saveSocialPost(accessToken, key);
        setSavedLocal(key, true);
        return true;
      } catch (err) {
        setSavedLocal(key, currentlySaved);
        throw err;
      }
    },
    [accessToken, isSaved, setSavedLocal]
  );

  const value = useMemo(
    () => ({
      ensureStatus,
      isSaved,
      toggleSaved,
      setSavedLocal,
    }),
    [ensureStatus, isSaved, toggleSaved, setSavedLocal]
  );

  return (
    <SocialSavedPostsContext.Provider value={value}>
      {children}
    </SocialSavedPostsContext.Provider>
  );
}

export function useSocialSavedPosts() {
  const ctx = useContext(SocialSavedPostsContext);
  if (!ctx) {
    return {
      ensureStatus: () => {},
      isSaved: () => false,
      toggleSaved: async () => false,
      setSavedLocal: () => {},
    };
  }
  return ctx;
}
